import { create } from 'zustand';
import { Timestamp } from '@firebase/firestore';
import { InsuranceChecklist, StakeholderType, ChecklistItem } from '../types';
import { checklistsApi } from '../api/checklists';
import { getErrorMessage } from '../utils/errorHandler';
import { withLoading } from './utils/withLoading';

interface AgentChecklistsState {
  checklists: InsuranceChecklist[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  fetchChecklists: (insurerName: string) => Promise<void>;
  createChecklist: (
    insurerName: string,
    data: {
      stakeholderType: StakeholderType;
      serviceAreaIds?: string[];
      items: ChecklistItem[];
      reportThresholds?: number[];
    }
  ) => Promise<InsuranceChecklist>;
  updateChecklist: (
    checklistId: string,
    data: {
      stakeholderType?: StakeholderType;
      serviceAreaIds?: string[];
      items?: ChecklistItem[];
      reportThresholds?: number[];
    }
  ) => Promise<void>;
  deleteChecklist: (checklistId: string) => Promise<void>;
  countStructuresUsing: (checklistId: string) => Promise<number>;
  clearError: () => void;
}

export const useAgentChecklistsStore = create<AgentChecklistsState>((set) => ({
  checklists: [],
  isLoading: false,
  isSaving: false,
  error: null,

  fetchChecklists: async (insurerName: string) => {
    await withLoading(set, 'isLoading', async () => {
      const checklists = await checklistsApi.getChecklistsByInsurer(insurerName);
      set({ checklists });
    });
  },

  createChecklist: async (insurerName, data) => {
    set({ isSaving: true, error: null });
    try {
      const created = await checklistsApi.createChecklist({
        insurerName,
        ...data,
      });
      set((state) => ({
        checklists: [...state.checklists, created],
        isSaving: false,
      }));
      return created;
    } catch (error) {
      set({ error: getErrorMessage(error), isSaving: false });
      throw error;
    }
  },

  updateChecklist: async (checklistId, data) => {
    set({ isSaving: true, error: null });
    try {
      await checklistsApi.updateChecklist(checklistId, data);
      set((state) => ({
        checklists: state.checklists.map((c) =>
          c.id === checklistId ? { ...c, ...data, updatedAt: Timestamp.now() } : c
        ),
        isSaving: false,
      }));
    } catch (error) {
      set({ error: getErrorMessage(error), isSaving: false });
      throw error;
    }
  },

  deleteChecklist: async (checklistId) => {
    set({ isSaving: true, error: null });
    try {
      await checklistsApi.deleteChecklist(checklistId);
      set((state) => ({
        checklists: state.checklists.filter((c) => c.id !== checklistId),
        isSaving: false,
      }));
    } catch (error) {
      set({ error: getErrorMessage(error), isSaving: false });
      throw error;
    }
  },

  countStructuresUsing: async (checklistId) => {
    return checklistsApi.countStructuresUsingChecklist(checklistId);
  },

  clearError: () => set({ error: null }),
}));
